import { useEffect, useState, useMemo } from "react";
import {
  AlertTriangle,
  ShieldCheck,
  Clock,
  ExternalLink,
  Radio,
  TrendingUp,
  Eye,
  TriangleAlert,
  Info,
  RefreshCw,
  LifeBuoy,
  MapPin,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "@/components/location-provider";
import { PageHeader } from "@/components/page-header";
import { DrivingSafetyCard } from "@/components/dashboard/driving-safety-card";

interface Alert {
  event: string;
  description: string;
  sender_name: string;
  start: number;
  end: number;
  tags?: string[];
  headline?: string;
  instruction?: string;
  areaDesc?: string;
  severity?: "extreme" | "severe" | "moderate" | "minor" | "unknown";
  source?: "nws" | "ec" | "owm";
}

function extractTitle(alert: Alert): string {
  const generic = ["weather", "alert", "warning", "watch", "advisory"];
  if (!generic.includes(alert.event.toLowerCase())) return alert.event;
  const first = alert.description.split(/[.\n]/)[0].trim();
  if (first.length > 5 && first.length < 80) return first;
  return `${alert.sender_name || "Weather"} Alert`;
}

function formatDescription(raw: string): string {
  let text = raw.replace(/###\s*/g, "").replace(/\s{2,}/g, " ").trim();
  text = text.replace(/\.\s+([a-z])/g, (_, c) => `. ${c.toUpperCase()}`);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const SECTION_ACCENTS: Record<string, string> = {
  WHAT: "text-red-300/90",
  IMPACTS: "text-orange-300/90",
  WHEN: "text-amber-300/90",
  WHERE: "text-sky-300/90",
  "ADDITIONAL DETAILS": "text-foreground/60",
};

function parseAlertSections(raw: string): { label: string; body: string }[] {
  const cleaned = raw
    .replace(/\*\*\*/g, "")
    .replace(/\$\$+/g, "")
    .replace(/###\s*/g, "")
    .trim();
  const sections: { label: string; body: string }[] = [];
  const parts = cleaned
    .split(/(?=\n\s*\*\s*[A-Z][A-Z \/&'-]*?\.\.\.)/)
    .map((p) => p.trim().replace(/^\*\s*/, ""))
    .filter(Boolean);

  for (const part of parts) {
    const match = part.match(/^([A-Z][A-Z \/&'-]{0,40}?)\.\.\.\s*([\s\S]*)$/);
    if (match) {
      sections.push({ label: match[1].trim(), body: match[2].trim() });
    } else if (sections.length > 0) {
      sections[sections.length - 1].body += " " + part;
    } else {
      sections.push({ label: "", body: part });
    }
  }
  return sections;
}

function renderInline(text: string): React.ReactNode[] {
  return text.split(/\*\*/).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

function tagVariant(tag: string): "destructive" | "warning" | "info" | "outline" {
  const t = tag.toLowerCase();
  if (
    t.includes("extreme") ||
    t.includes("severe") ||
    t.includes("emergency") ||
    t.includes("catastrophic") ||
    t.includes("tornado") ||
    t.includes("hurricane") ||
    (t.includes("warning") && !t.includes("frost warning") && !t.includes("fog warning"))
  ) {
    return "destructive";
  }
  if (t.includes("watch")) return "warning";
  if (t.includes("advisory")) return "info";
  return "outline";
}

function AlertDescription({ raw }: { raw: string }) {
  const sections = parseAlertSections(raw);
  const hasLabels = sections.some((s) => s.label);

  if (!hasLabels) {
    const paragraphs = formatDescription(raw)
      .split(/(?<=\.)\s+/)
      .reduce((acc: string[][], sentence, idx) => {
        const pIdx = Math.floor(idx / 3);
        if (!acc[pIdx]) acc[pIdx] = [];
        acc[pIdx].push(sentence);
        return acc;
      }, []);
    return (
      <div className="space-y-2.5">
        {paragraphs.map((sentences, pIdx) => (
          <p key={pIdx}>{sentences.join(" ")}</p>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sections.map((s, i) =>
        s.label ? (
          <div key={i}>
            <p
              className={`text-[11px] font-bold uppercase tracking-wider mb-0.5 ${
                SECTION_ACCENTS[s.label] || "text-foreground/50"
              }`}
            >
              {s.label}
            </p>
            <p>{renderInline(s.body)}</p>
          </div>
        ) : (
          <p key={i}>{renderInline(s.body)}</p>
        )
      )}
    </div>
  );
}

function formatTime(ts: number) {
  return new Date(ts * 1000).toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(start: number, end: number): string {
  const hours = Math.round((end - start) / 3600);
  if (hours < 1) return "<1h";
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

function isCurrentlyActive(start: number, end: number): boolean {
  const now = Math.floor(Date.now() / 1000);
  return now >= start && now <= end;
}

function isUpcoming(start: number): boolean {
  return Math.floor(Date.now() / 1000) < start;
}

type Severity = "severe" | "warning" | "watch" | "advisory" | "statement";

function severityInfo(
  event: string,
  tags: string[],
  official?: Alert["severity"]
): {
  variant: "destructive" | "warning" | "outline" | "info";
  label: string;
  severity: Severity;
  accent: string;
  bgColor: string;
  borderColor: string;
  icon: React.ElementType;
} {
  if (official) {
    const map: Record<NonNullable<Alert["severity"]>, Severity> = {
      extreme: "severe",
      severe: "severe",
      moderate: "warning",
      minor: "advisory",
      unknown: "statement",
    };
    const sev = map[official];
    const styles: Record<Severity, { variant: "destructive" | "warning" | "info" | "outline"; label: string; accent: string; bgColor: string; borderColor: string; icon: React.ElementType }> = {
      severe: { variant: "destructive", label: "Severe", accent: "bg-red-500", bgColor: "bg-red-500/5", borderColor: "border-red-500/30", icon: TriangleAlert },
      warning: { variant: "destructive", label: "Warning", accent: "bg-orange-500", bgColor: "bg-orange-500/5", borderColor: "border-orange-500/30", icon: AlertTriangle },
      watch: { variant: "warning", label: "Watch", accent: "bg-amber-500", bgColor: "bg-amber-500/5", borderColor: "border-amber-500/30", icon: Eye },
      advisory: { variant: "info", label: "Advisory", accent: "bg-sky-500", bgColor: "bg-sky-500/5", borderColor: "border-sky-500/30", icon: Info },
      statement: { variant: "outline", label: "Statement", accent: "bg-slate-500", bgColor: "bg-slate-500/5", borderColor: "border-slate-500/30", icon: Info },
    };
    return { ...styles[sev], severity: sev };
  }
  const fullText = [event, ...tags].join(" ").toLowerCase();
  if (
    fullText.includes("extreme") ||
    fullText.includes("emergency") ||
    fullText.includes("catastrophic") ||
    fullText.includes("tornado") ||
    fullText.includes("hurricane")
  ) {
    return {
      variant: "destructive",
      label: "Severe",
      severity: "severe",
      accent: "bg-red-500",
      bgColor: "bg-red-500/5",
      borderColor: "border-red-500/30",
      icon: TriangleAlert,
    };
  }
  if (
    fullText.includes("warning") &&
    !fullText.includes("frost warning") &&
    !fullText.includes("fog warning")
  ) {
    return {
      variant: "destructive",
      label: "Warning",
      severity: "warning",
      accent: "bg-orange-500",
      bgColor: "bg-orange-500/5",
      borderColor: "border-orange-500/30",
      icon: AlertTriangle,
    };
  }
  if (
    fullText.includes("severe") ||
    /\b\d{2,3}\s*(?:to|-)\s*\d{2,3}\s*mm\b/.test(fullText) ||
    /rainfall.*\d{2,}/.test(fullText)
  ) {
    return {
      variant: "destructive",
      label: "Severe",
      severity: "severe",
      accent: "bg-red-500",
      bgColor: "bg-red-500/5",
      borderColor: "border-red-500/30",
      icon: TriangleAlert,
    };
  }
  if (fullText.includes("watch")) {
    return {
      variant: "warning",
      label: "Watch",
      severity: "watch",
      accent: "bg-amber-500",
      bgColor: "bg-amber-500/5",
      borderColor: "border-amber-500/30",
      icon: Eye,
    };
  }
  if (fullText.includes("advisory") || fullText.includes("moderate")) {
    return {
      variant: "info",
      label: "Advisory",
      severity: "advisory",
      accent: "bg-sky-500",
      bgColor: "bg-sky-500/5",
      borderColor: "border-sky-500/30",
      icon: Info,
    };
  }
  return {
    variant: "outline",
    label: "Statement",
    severity: "statement",
    accent: "bg-slate-500",
    bgColor: "bg-slate-500/5",
    borderColor: "border-slate-500/30",
    icon: Info,
  };
}

const severityOrder: Record<Severity, number> = {
  severe: 0,
  warning: 1,
  watch: 2,
  advisory: 3,
  statement: 4,
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [current, setCurrent] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const { current: location } = useLocation();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    fetch(`/api/alerts?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Alerts API ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (cancelled) return;
        setAlerts(d.alerts || []);
        setCurrent(d.current || null);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
        setAlerts([]);
        setCurrent(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [location.lat, location.lon, refresh]);

  const sorted = useMemo(
    () =>
      [...alerts].sort((a, b) => {
        const sa = severityInfo(a.event + " " + a.description, a.tags || [], a.severity);
        const sb = severityInfo(b.event + " " + b.description, b.tags || [], b.severity);
        return severityOrder[sa.severity] - severityOrder[sb.severity];
      }),
    [alerts]
  );

  const counts = useMemo(() => {
    const c = { severe: 0, warning: 0, watch: 0, advisory: 0, statement: 0 };
    alerts.forEach((a) => {
      const info = severityInfo(a.event + " " + a.description, a.tags || [], a.severity);
      c[info.severity]++;
    });
    return c;
  }, [alerts]);

  const hasSevere = counts.severe > 0 || counts.warning > 0;

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="Operational"
        title="Alerts"
        subtitle={`${location.name}, ${location.country}`}
        action={
          <GlassPanel
            intensity="low"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs !rounded-full"
          >
            <Radio
              className={`h-3 w-3 ${alerts.length ? (hasSevere ? "text-destructive animate-pulse" : "text-amber-400") : "text-emerald-400"}`}
            />
            <span className="font-semibold">
              {alerts.length
                ? hasSevere
                  ? `${alerts.length} active — urgent`
                  : `${alerts.length} active`
                : "All clear"}
            </span>
          </GlassPanel>
        }
      />

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-72 rounded-2xl" />
            <Skeleton className="h-72 rounded-2xl" />
          </div>
        </div>
      ) : loadError ? (
        <GlassPanel intensity="low" className="p-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-amber-500/10 mx-auto mb-5">
            <AlertTriangle className="h-10 w-10 text-amber-400" />
          </div>
          <h2 className="text-xl font-bold">Couldn't load alerts</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
            We couldn't reach the weather service for {location.name}. This may be temporary.
          </p>
          <button
            type="button"
            onClick={() => setRefresh((n) => n + 1)}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary/10 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/15 transition-colors cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Try again
          </button>
        </GlassPanel>
      ) : (
        <>
          {current && <DrivingSafetyCard current={current as never} />}
          {alerts.length === 0 ? (
            <GlassPanel intensity="low" className="p-16 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-emerald-500/10 mx-auto mb-5">
                <ShieldCheck className="h-10 w-10 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold">All Clear</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">
                No active weather alerts for {location.name}. Conditions are safe.
              </p>
            </GlassPanel>
          ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: "severe" as const, label: "Severe", icon: TriangleAlert },
              { key: "warning" as const, label: "Warning", icon: AlertTriangle },
              { key: "watch" as const, label: "Watch", icon: Eye },
              { key: "advisory" as const, label: "Advisory", icon: Info },
            ]
              .filter((s) => counts[s.key] > 0)
              .map((s) => (
                <GlassPanel
                  key={s.key}
                  intensity="low"
                  className={`p-3.5 flex items-center gap-3 ${
                    s.key === "severe"
                      ? "!border-red-500/20"
                      : s.key === "warning"
                        ? "!border-orange-500/20"
                        : s.key === "watch"
                          ? "!border-amber-500/20"
                          : "!border-sky-500/20"
                  }`}
                >
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                      s.key === "severe"
                        ? "bg-red-500/10"
                        : s.key === "warning"
                          ? "bg-orange-500/10"
                          : s.key === "watch"
                            ? "bg-amber-500/10"
                            : "bg-sky-500/10"
                    }`}
                  >
                    <s.icon
                      className={`h-4.5 w-4.5 ${
                        s.key === "severe"
                          ? "text-red-400"
                          : s.key === "warning"
                            ? "text-orange-400"
                            : s.key === "watch"
                              ? "text-amber-400"
                              : "text-sky-400"
                      }`}
                    />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{counts[s.key]}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</p>
                  </div>
                </GlassPanel>
              ))}
          </div>

          <div className={`grid gap-4 ${alerts.length > 1 ? "lg:grid-cols-2" : ""}`}>
            {sorted.map((alert, i) => {
              const sev = severityInfo(alert.event + " " + alert.description, alert.tags || [], alert.severity);
              const active = isCurrentlyActive(alert.start, alert.end);
              const upcoming = isUpcoming(alert.start);
              const SevIcon = sev.icon;
              return (
                <GlassPanel
                  key={i}
                  intensity="medium"
                  className={`relative ${sev.bgColor} ${sev.borderColor}`}
                >
                  <div
                    className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${sev.accent}`}
                  />

                  <div className="px-5 pt-4 pb-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge variant={sev.variant} size="sm">
                            <AlertTriangle className="h-2.5 w-2.5 mr-1 inline" />
                            {sev.label}
                          </Badge>
                          {active && (
                            <Badge variant="success" size="sm">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 mr-1 inline-block animate-pulse" />
                              Active
                            </Badge>
                          )}
                          {upcoming && !active && (
                            <Badge variant="outline" size="sm">
                              <Clock className="h-2.5 w-2.5 mr-1 inline" />
                              Upcoming
                            </Badge>
                          )}
                          {alert.tags?.slice(0, 2).map((tag) => (
                            <Badge key={tag} variant={tagVariant(tag)} size="sm">
                              {tag.toLowerCase()}
                            </Badge>
                          ))}
                          {alert.source === "nws" && (
                            <Badge variant="secondary" size="sm">NWS</Badge>
                          )}
                          {alert.source === "ec" && (
                            <Badge variant="secondary" size="sm">Environment Canada</Badge>
                          )}
                        </div>
                        <h2 className="text-base font-bold leading-tight">
                          {extractTitle(alert)}
                        </h2>
                        {alert.headline && alert.headline !== alert.event && (
                          <p className="text-xs text-muted-foreground mt-1">{alert.headline}</p>
                        )}
                      </div>
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${
                          sev.severity === "severe"
                            ? "bg-red-500/10"
                            : sev.severity === "warning"
                              ? "bg-orange-500/10"
                              : sev.severity === "watch"
                                ? "bg-amber-500/10"
                                : "bg-sky-500/10"
                        }`}
                      >
                        <SevIcon
                          className={`h-4 w-4 ${
                            sev.severity === "severe"
                              ? "text-red-400"
                              : sev.severity === "warning"
                                ? "text-orange-400"
                                : sev.severity === "watch"
                                  ? "text-amber-400"
                                  : "text-sky-400"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="px-5 py-3 border-t border-border/15">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground mb-3">
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        <span className="font-medium text-foreground/70">{formatTime(alert.start)}</span>
                        <span className="text-muted-foreground/50">→</span>
                        <span className="font-medium text-foreground/70">{formatTime(alert.end)}</span>
                        <span className="ml-0.5 px-1.5 py-0.5 rounded bg-muted/50 text-[10px] font-semibold">
                          {formatDuration(alert.start, alert.end)}
                        </span>
                      </span>
                      {alert.sender_name && (
                        <span className="font-medium italic">{alert.sender_name}</span>
                      )}
                      {alert.areaDesc && (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-3 w-3" />
                          <span>{alert.areaDesc}</span>
                        </span>
                      )}
                    </div>

                    <div className="text-sm leading-relaxed text-foreground/80">
                      <AlertDescription raw={alert.description} />
                    </div>

                    {alert.instruction && (
                      <div className="mt-3 rounded-lg border border-sky-500/20 bg-sky-500/5 p-3 text-xs leading-relaxed text-foreground/85">
                        <p className="flex items-center gap-1.5 font-bold text-sky-300/90 mb-1 uppercase tracking-wider text-[10px]">
                          <LifeBuoy className="h-3 w-3" />
                          What to do
                        </p>
                        <p className="whitespace-pre-line">{alert.instruction}</p>
                      </div>
                    )}

                    {alert.description.match(/https?:\/\/[^\s]+/g)?.map((url, j) => (
                      <a
                        key={j}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-4 text-xs text-primary hover:underline font-medium"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {url.replace(/https?:\/\//, "").split("/")[0]}
                      </a>
                    ))}
                  </div>
                </GlassPanel>
              );
            })}
          </div>
        </>
          )}
        </>
      )}
    </div>
  );
}